import { createCookie } from 'helpers/utils';

/**
 * Serviço de autenticação OAuth2
 * Gerencia o fluxo de autenticação usando Authorization Code Grant
 */
class AuthService {
  /**
   * Gera a URL de autorização OAuth2
   * @param {string} state - Estado único para prevenir CSRF
   * @returns {string} URL de autorização completa
   */
  static getAuthorizationUrl(state) {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: process.env.REACT_APP_CLIENT_NAME || 'mspdesk-web',
      state: state,
      redirect_uri: process.env.REACT_APP_REDIRECT_URI,
      scope: 'READ WRITE'
    });

    return `${
      process.env.REACT_APP_BASE_URL
    }/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Troca o código de autorização por um token de acesso
   * @param {string} code - Código de autorização recebido do servidor OAuth2
   * @returns {Promise<{access_token: string, token_type?: string, expires_in?: number}>}
   * @throws {Error} Se a troca do token falhar
   */
  static async exchangeCodeForToken(code) {
    if (!code) {
      throw new Error('Authorization code is required');
    }

    const credentials = btoa(
      `${process.env.REACT_APP_CLIENT_NAME}:${process.env.REACT_APP_CLIENT_SECRET}`
    );

    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('code', code);
    params.append('redirect_uri', process.env.REACT_APP_REDIRECT_URI);

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/oauth2/token`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error_description ||
            errorData.error ||
            `Failed to exchange authorization code: ${response.status} ${response.statusText}`
        );
      }

      const tokenData = await response.json();

      if (!tokenData.access_token) {
        throw new Error('Invalid token response: access_token is missing');
      }

      return tokenData;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error while exchanging authorization code');
    }
  }

  /**
   * Salva o token de acesso no cookie
   * @param {string} token - Token de acesso
   */
  static saveToken(token) {
    if (!token) {
      throw new Error('Token is required');
    }
    createCookie('token', token);
  }

  /**
   * Valida o código de autorização na URL
   * @param {URLSearchParams} searchParams - Parâmetros da URL
   * @returns {{code: string, state: string|null}|null} Objeto com code e state ou null se inválido
   */
  static validateAuthorizationCode(searchParams) {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      throw new Error(errorDescription || error || 'Authorization failed');
    }

    if (!code) {
      return null;
    }

    return {
      code,
      state: searchParams.get('state')
    };
  }
}

export default AuthService;
